import React, { useState, useEffect, useMemo } from 'react';
import { Cloud } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { analyticsAPI } from '../../../services/api';

const NuvemPalavras = ({ filtros }) => {
  const { darkMode } = useTheme();
  const [palavras, setPalavras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredWord, setHoveredWord] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    const params = {};
    if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros?.dataFim) params.dataFim = filtros.dataFim;
    if (filtros?.instanciaId) params.instanciaId = filtros.instanciaId;

    analyticsAPI.getPalavrasChave(clienteId, params)
      .then(res => setPalavras(res.data || []))
      .catch(() => setPalavras([]))
      .finally(() => setLoading(false));
  }, [clienteId, filtros?.dataInicio, filtros?.dataFim, filtros?.instanciaId]);

  const corSentimento = (s) => {
    if (s === 'positivo') return darkMode ? 'text-green-400' : 'text-green-600';
    if (s === 'negativo') return darkMode ? 'text-red-400' : 'text-red-600';
    return darkMode ? 'text-gray-400' : 'text-gray-500';
  };

  const bgSentimento = (s) => {
    if (s === 'positivo') return darkMode ? 'bg-green-900/20' : 'bg-green-50';
    if (s === 'negativo') return darkMode ? 'bg-red-900/20' : 'bg-red-50';
    return darkMode ? 'bg-zinc-700/50' : 'bg-gray-100';
  };

  // Calcular tamanhos relativos
  const wordCloud = useMemo(() => {
    if (!palavras.length) return [];
    const max = Math.max(...palavras.map(p => p.total));
    const min = Math.min(...palavras.map(p => p.total));
    const range = max - min || 1;

    return palavras.map(p => ({
      ...p,
      size: 0.75 + ((p.total - min) / range) * 1.75, // 0.75rem a 2.5rem
      opacity: 0.5 + ((p.total - min) / range) * 0.5,
    }));
  }, [palavras]);

  if (loading) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl animate-pulse ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        <div className={`h-5 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded w-1/4 mb-6`}></div>
        <div className={`h-48 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded`}></div>
      </div>
    );
  }

  if (palavras.length === 0) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold flex items-center gap-2 mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Cloud className="w-5 h-5" /> Palavras-chave
        </h3>
        <p className={`text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Nenhuma palavra-chave encontrada no período
        </p>
      </div>
    );
  }

  return (
    <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-base sm:text-lg font-semibold flex items-center gap-2 mb-4 sm:mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <Cloud className="w-4 h-4 sm:w-5 sm:h-5" /> Palavras-chave
        <span className={`text-xs font-normal px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
          {palavras.length} termos
        </span>
      </h3>

      {/* Nuvem */}
      <div className="flex flex-wrap items-center justify-center gap-3 py-4 min-h-[200px]">
        {wordCloud.map((w, i) => (
          <span
            key={i}
            onMouseEnter={() => setHoveredWord(w)}
            onMouseLeave={() => setHoveredWord(null)}
            className={`inline-block px-3 py-1.5 rounded-full cursor-default transition-colors duration-200 font-medium
              ${bgSentimento(w.sentimento_predominante)} ${corSentimento(w.sentimento_predominante)}
              ${hoveredWord?.palavra === w.palavra ? 'shadow-lg ring-2 ring-blue-500/50' : ''}
            `}
            style={{
              fontSize: `${w.size}rem`,
              opacity: hoveredWord ? (hoveredWord.palavra === w.palavra ? 1 : 0.3) : w.opacity,
            }}
          >
            {w.palavra}
          </span>
        ))}
      </div>

      {/* Tooltip fixo */}
      <div className={`mt-4 p-3 rounded-lg text-center text-sm h-12 flex items-center justify-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
        {hoveredWord ? (
          <>
            <span className={`font-semibold ${corSentimento(hoveredWord.sentimento_predominante)}`}>
              "{hoveredWord.palavra}"
            </span>
            <span className={` ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              — {hoveredWord.total}x mencionada · sentimento {hoveredWord.sentimento_predominante}
            </span>
          </>
        ) : (
          <span className={`${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>Passe o mouse sobre um termo</span>
        )}
      </div>

      {/* Legenda */}
      <div className={`flex items-center justify-center gap-6 mt-4 pt-4 border-t text-xs ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-green-500"></span> Positivo
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded-full ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span> Neutro
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-500"></span> Negativo
        </span>
        <span>Tamanho = frequência</span>
      </div>
    </div>
  );
};

export default NuvemPalavras;
